import { Result, ok } from "neverthrow"
import { BaseRunContext } from "../scriptevaluator/RunContext"
import { CalcType } from "../scriptcompiler/CompilerTypes"
import { CalcRuntimeError, CompiledCalcEvaluateOk } from "../scriptevaluator/RuntimeTypes"

// calculate function success response
export type ExecutableCalcCalculateOk = CompiledCalcEvaluateOk

// calculate function
export type ExecutableCalcCalculateFunction = (context: BaseRunContext) => Result<ExecutableCalcCalculateOk, CalcRuntimeError>

// "default" calculations can be registered when a fallback calculation is
// wanted in the event that an outputCid + calcType does not have a custom
// calc specified; the key example of this is the default validation calc which
// reads the validation properties of the Concept in question—  but only if
// a custom validation calc is not specified for the outputCid
export enum ExecutableCalcPrecedence {
	Primary = 1,
	Default = 2,
}

// used by CalcRegistry to ensure there is not multiple calculations
// handling the same concern—  that is, a single answer for the value,
// validity or activation of a specific Concept
export const executableCalcKey = (outputCid: string, calcType: CalcType, precedence: ExecutableCalcPrecedence) => [outputCid, calcType, precedence].join('|')

interface ExecutableCalcConstructorArgs {
	calcType: CalcType
	precedence?: ExecutableCalcPrecedence,
	outputCid: string
	inputCids: string[]
	// memoized selectors allow the redux store to update the calculations
	// reactively & automatically;
	// for most calcs you can just define the calculate function and then
	// provide an arrow that returns it, e.g.
	//
	// const calculate: ExecutableCalcCalculateFunction = (ctx) => {
	//     return ok({
	//         calcType: CalcType.Value,
	//         returnValue: [{value: 'lorem ipsum'}]
	//     })
	// }
	// const selectCalculate = () => calculate
	selectCalculate: () => ExecutableCalcCalculateFunction
}

export class ExecutableCalc {
	private _calcKey: string
	get calcKey(): string {
		return this._calcKey
	}
	private _calcType: CalcType
	get calcType(): CalcType {
		return this._calcType
	}
	private _precedence: ExecutableCalcPrecedence
	get precedence(): ExecutableCalcPrecedence {
		return this._precedence
	}
	private _outputCid: string
	get outputCid(): string {
		return this._outputCid
	}
	private _inputCids: string[]
	get inputCids(): string[] {
		return this._inputCids
	}
	private _selectCalculate: () => ExecutableCalcCalculateFunction
	// to execute the calc:
	//
	// const rExec = calc.calculate(ctx)
	// if(rExec.isErr()) {
	//     ...
	get calculate(): ExecutableCalcCalculateFunction {
		return this._selectCalculate()
	}
	constructor({calcType, precedence, outputCid, inputCids, selectCalculate}: ExecutableCalcConstructorArgs) {
		precedence = precedence ?? ExecutableCalcPrecedence.Primary
		this._calcKey = executableCalcKey(outputCid, calcType, precedence)
		this._calcType = calcType
		this._precedence = precedence
		this._outputCid = outputCid
		this._inputCids = inputCids
		this._selectCalculate = selectCalculate
	}
}
